import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, Loader2, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== FORCED CHANGE PASSWORD SCREEN (first login / admin reset) =====
export function ChangePasswordGate() {
  const { user, refreshUser, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("Bagong password ay dapat at least 6 characters");
    if (next !== confirm) return toast.error("Hindi magkatugma ang bagong password");
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next });
      toast.success("✅ Password na-update na!");
      await refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gradient-to-br from-red-900 via-red-800 to-gray-900 p-4" data-testid="change-password-gate">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 shadow-2xl">
        <div className="flex justify-center mb-4">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
            <KeyRound className="h-8 w-8 text-red-600" />
          </div>
        </div>
        <h2 className="text-xl font-bold text-center text-gray-900 mb-1">Palitan ang Iyong Password</h2>
        <p className="text-sm text-center text-gray-600 mb-6">
          Hi <strong>{user?.name || user?.email?.split("@")[0]}</strong>! Para sa seguridad, kailangan mong gumawa ng sarili mong password bago magpatuloy.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Kasalukuyang Password</Label>
            <div className="relative">
              <Input type={show ? "text" : "password"} value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Current / temporary password" required data-testid="cp-current" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Bagong Password (min 6 characters)</Label>
            <div className="relative">
              <Input type={show ? "text" : "password"} value={next} onChange={(e) => setNext(e.target.value)} placeholder="New password" required data-testid="cp-new" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Ulitin ang Bagong Password</Label>
            <Input type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" required data-testid="cp-confirm" />
          </div>
          <button type="button" onClick={() => setShow((s) => !s)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
            {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {show ? "Itago" : "Ipakita"} ang password
          </button>
          <Button type="submit" disabled={saving} className="w-full bg-red-600 hover:bg-red-700" data-testid="cp-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            I-save ang Bagong Password
          </Button>
        </form>
        <button onClick={logout} className="w-full text-xs text-gray-400 hover:text-gray-600 mt-4" data-testid="cp-logout">
          Mag-logout muna
        </button>
      </div>
    </div>
  );
}

// ===== DISCREPANCY LOCKOUT SCREEN (branch has OPEN discrepancy) =====
export function DiscrepancyLockGate({ lockData, onResolved }) {
  const [resolvingId, setResolvingId] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const resolve = async (id) => {
    if (reason.trim().length < 5) return toast.error("Kailangan ng explanation (min 5 characters)");
    setSaving(true);
    try {
      await axios.put(`${API}/stock-discrepancies/${id}/resolve`, { resolution: reason });
      toast.success("✅ Discrepancy resolved!");
      setResolvingId(null);
      setReason("");
      await onResolved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resolve");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gradient-to-br from-red-950 via-red-900 to-gray-900 p-4 overflow-y-auto" data-testid="discrepancy-lock-gate">
      <div className="max-w-2xl w-full bg-white rounded-2xl p-8 shadow-2xl my-8">
        <div className="flex justify-center mb-4">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center animate-pulse">
            <ShieldAlert className="h-8 w-8 text-red-600" />
          </div>
        </div>
        <h2 className="text-xl font-bold text-center text-gray-900 mb-1">🔒 Naka-lock ang System</h2>
        <p className="text-sm text-center text-gray-600 mb-2">
          May <strong className="text-red-600">{lockData.open_count}</strong> na hindi pa na-reresolve na stock discrepancy sa branch mo (<strong>{lockData.branches.join(", ")}</strong>).
        </p>
        <p className="text-xs text-center text-gray-500 mb-6">
          Hindi mo maa-access ang system hangga&apos;t hindi mo naaayos ang mga ito. I-explain kung ano ang nangyari sa bawat isa para ma-unlock.
        </p>

        <div className="space-y-3 max-h-[50vh] overflow-y-auto" data-testid="lock-discrepancy-list">
          {lockData.discrepancies.map((d) => (
            <div key={d.id} className="border-2 border-red-200 bg-red-50 rounded-lg p-4" data-testid={`lock-disc-${d.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-xs font-bold px-2 py-0.5 rounded bg-red-600 text-white">
                  {d.type === "TRANSFER_VARIANCE" ? "🚚 Transfer Variance" : "❓ Unexplained Loss"}
                </span>
                <span className="font-semibold text-gray-900">{d.item_name}</span>
              </div>
              <div className="text-sm text-gray-700">
                {d.type === "TRANSFER_VARIANCE" ? (
                  <>Pinadala: <strong>{d.sent_qty}</strong> • Natanggap: <strong>{d.received_qty}</strong> • <span className="text-red-700 font-bold">Kulang: {d.variance_qty}</span></>
                ) : (
                  <>Expected: <strong>{d.expected_qty}</strong> • Actual: <strong>{d.actual_qty}</strong> • <span className="text-red-700 font-bold">Diff: {d.variance_qty}</span></>
                )}
              </div>
              {d.reason && <p className="text-xs text-gray-500 mt-1">Note: {d.reason}</p>}

              {resolvingId === d.id ? (
                <div className="mt-3 flex items-center gap-2">
                  <Input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Ano ang nangyari? (min 5 chars)"
                    className="h-9 text-sm"
                    data-testid={`lock-reason-${d.id}`}
                  />
                  <Button size="sm" disabled={saving} onClick={() => resolve(d.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`lock-confirm-${d.id}`}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resolve"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setResolvingId(null); setReason(""); }}>Cancel</Button>
                </div>
              ) : (
                <Button size="sm" className="mt-3 bg-red-600 hover:bg-red-700" onClick={() => { setResolvingId(d.id); setReason(""); }} data-testid={`lock-resolve-${d.id}`}>
                  I-resolve ito
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ===== Hook: fetch discrepancy lock status for current user =====
// The backend decides who is locked (owner is never locked; admins locked by ANY
// open discrepancy; branch users by their branch's). We simply trust its response.
export function useDiscrepancyLock(user) {
  const [lockData, setLockData] = useState(null);

  const check = useCallback(async () => {
    if (!user) {
      setLockData({ locked: false });
      return;
    }
    try {
      const res = await axios.get(`${API}/stock-discrepancies/my-lock`);
      setLockData(res.data);
    } catch (e) {
      setLockData({ locked: false });
    }
  }, [user]);

  useEffect(() => {
    check();
  }, [check]);

  return { lockData, recheck: check };
}
